// Fixes and prunes the "More by <author>" links (blocks/author-summary.liquid).
//
// Each link points to the catalog filtered to one author. Shopify's March/April
// 2026 storefront-filter change replaced the value used for metaobject-reference
// filters — the author's `gid://shopify/Metaobject/...` id was swapped for a
// Search & Discovery stable id (`gid://shopify/FilterSettingGroup/...`). That
// stable id isn't knowable from the author metaobject in Liquid, so the URL the
// snippet builds server-side no longer applies the filter and the link lands on
// the unfiltered catalog. We repair it at runtime: read the live author facet on
// the all-products collection, match it to the author by display name, and
// rewrite the link's href with the current filter value.
//
// We also keep the original behaviour of hiding a link when the author has no
// product other than the one on this page — there's nothing else to see.
//
// Fails open: if the facet can't be read or the author can't be matched, the
// link is left exactly as rendered (never removed) so a resolution miss can't
// make the button disappear. A link is only removed once we have a working
// filter URL AND confirm the author has no product besides the current one.

// The person-backed facets a "More by" author can live in. The store may expose
// a dedicated Authors facet, a unified Contributors facet, or both.
const AUTHOR_FACET_KEYS = ['custom.authors', 'custom.contributors'];

// collection pathname -> Promise<Map<normalized author label, {name, value}>>
const facetMaps = new Map();
// corrected filter URL -> Promise<boolean> (does a product other than current exist)
const otherProducts = new Map();

function normalize(text) {
  return (text || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

// Read the person-backed facet(s) from the all-products collection once and map
// each author's display label to the param name + value its checkbox submits
// today. A dedicated Authors facet wins over the unified Contributors facet.
function loadFacetMap(collectionUrl) {
  const key = collectionUrl.pathname;
  if (facetMaps.has(key)) return facetMaps.get(key);

  const promise = (async () => {
    const response = await fetch(collectionUrl.toString());
    if (!response.ok) return null;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const map = new Map();
    // The page renders each facet twice (desktop + drawer); keep the first
    // occurrence of each label, and let the dedicated Authors facet (first key)
    // take precedence over Contributors.
    AUTHOR_FACET_KEYS.forEach((facetKey) => {
      doc.querySelectorAll(`input[name*="${facetKey}"]`).forEach((input) => {
        const label = normalize(input.getAttribute('data-label') || input.getAttribute('aria-label'));
        const name = input.getAttribute('name');
        const value = input.getAttribute('value');
        if (label && name && value && !map.has(label)) {
          map.set(label, { name, value });
        }
      });
    });
    return map;
  })();

  facetMaps.set(key, promise);
  return promise;
}

function otherProductsExist(filterUrl, currentHandle) {
  if (otherProducts.has(filterUrl)) return otherProducts.get(filterUrl);

  const promise = (async () => {
    const response = await fetch(filterUrl);
    if (!response.ok) return true;

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const results = doc.getElementById('ResultsList');
    if (!results) return true;

    const handles = new Set();
    results.querySelectorAll('a[href*="/products/"]').forEach((anchor) => {
      const match = anchor.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (match) handles.add(match[1]);
    });
    handles.delete(currentHandle);
    return handles.size > 0;
  })();

  otherProducts.set(filterUrl, promise);
  return promise;
}

async function check(link) {
  if (link.dataset.moreByChecked) return;
  link.dataset.moreByChecked = '1';

  try {
    // The snippet's href already targets the localized all-products collection;
    // drop its (now stale) query to get the bare collection URL.
    const collectionUrl = new URL(link.getAttribute('href'), window.location.origin);
    collectionUrl.search = '';

    const facetMap = await loadFacetMap(collectionUrl);
    if (!facetMap) return; // fail open: couldn't read the facet

    const entry = facetMap.get(normalize(link.dataset.moreByAuthor));
    if (!entry) return; // fail open: leave the link rather than hide the button

    const filterUrl = new URL(collectionUrl);
    filterUrl.searchParams.set(entry.name, entry.value);
    const href = filterUrl.toString();
    link.setAttribute('href', href);

    const exists = await otherProductsExist(href, link.dataset.moreByCurrent || '');
    if (!exists) link.remove();
  } catch {
    /* fail open: leave the link */
  }
}

function init(root = document) {
  root.querySelectorAll('a[data-more-by]').forEach(check);
}

init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
